import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, ArrowLeft, CalendarBlank, ArrowsClockwise, DotsThreeVertical, PencilSimple, Trash } from "@phosphor-icons/react";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import { RiskBarChart, CycleTrendChart } from "@/components/Charts";
import ObservationsTable from "@/components/ObservationsTable";
import ActivityTimeline from "@/components/ActivityTimeline";
import ClientProfileTab from "@/components/ClientProfileTab";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [auditType, setAuditType] = useState("internal"); // internal | statutory
  const [activeCycleId, setActiveCycleId] = useState(null);
  const [metricsConsolidated, setMetricsConsolidated] = useState(null);
  const [metricsCycle, setMetricsCycle] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [tab, setTab] = useState("observations");
  const [openCycle, setOpenCycle] = useState(false);
  const [newCycleType, setNewCycleType] = useState("internal");
  const [newCycleMonth, setNewCycleMonth] = useState("April");
  const [newCycleYear, setNewCycleYear] = useState(new Date().getFullYear());
  const [newCycleFY, setNewCycleFY] = useState(() => {
    // Suggest current Indian FY, e.g. "FY 2025-26"
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const start = m >= 4 ? y : y - 1;
    return `FY ${start}-${String((start + 1) % 100).padStart(2, "0")}`;
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editIndustry, setEditIndustry] = useState("");

  const filteredCycles = cycles.filter(
    (c) => (c.cycle_type || "internal") === auditType
  );
  const activeCycle = cycles.find((c) => c.id === activeCycleId) || null;
  const activeCycleType = activeCycle?.cycle_type || "internal";

  // Cycle metadata (Prepared by / Reviewed by / Status label) — affects PDF cover
  const [cycleEditOpen, setCycleEditOpen] = useState(false);
  const [cyclePreparedBy, setCyclePreparedBy] = useState("");
  const [cycleReviewedBy, setCycleReviewedBy] = useState("");
  const [cycleStatusLabel, setCycleStatusLabel] = useState("");

  const openCycleEdit = () => {
    const c = cycles.find((x) => x.id === activeCycleId);
    if (!c) return;
    setCyclePreparedBy(c.prepared_by || "");
    setCycleReviewedBy(c.reviewed_by || "");
    setCycleStatusLabel(c.audit_status_label || "Final");
    setCycleEditOpen(true);
  };

  const saveCycleEdit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.patch(`/cycles/${activeCycleId}`, {
        prepared_by: cyclePreparedBy || null,
        reviewed_by: cycleReviewedBy || null,
        audit_status_label: cycleStatusLabel || null,
      });
      // Refresh cycle list so UI reflects new metadata
      setCycles((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
      toast.success("Report metadata updated");
      setCycleEditOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const openEdit = () => {
    setEditName(company?.name || "");
    setEditIndustry(company?.industry || "");
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put(`/companies/${companyId}`, {
        name: editName,
        industry: editIndustry,
      });
      setCompany(r.data);
      toast.success("Client updated");
      setEditOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const deleteCompanyAction = async () => {
    if (!company) return;
    const confirmed = window.prompt(
      `This permanently deletes "${company.name}" along with ALL its cycles, observations, resolutions, comments, attachments, and client users.\n\nType the company name to confirm:`
    );
    if (confirmed !== company.name) {
      if (confirmed !== null) toast.error("Name did not match — cancelled");
      return;
    }
    try {
      const r = await api.delete(`/companies/${companyId}`);
      toast.success(`Deleted '${company.name}' (${r.data?.cascade?.observations || 0} observations removed)`);
      navigate("/audit/companies");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const loadCompany = async () => {
    const c = await api.get(`/companies/${companyId}`);
    setCompany(c.data);
  };
  const loadCycles = async () => {
    const r = await api.get(`/cycles?company_id=${companyId}`);
    setCycles(r.data);
  };
  const loadConsolidated = async () => {
    const m = await api.get(
      `/analytics/risk-matrix?company_id=${companyId}&cycle_type=${auditType}`
    );
    setMetricsConsolidated(m.data);
  };
  const loadCycleMetrics = async () => {
    if (!activeCycleId) return;
    const m = await api.get(`/analytics/risk-matrix?company_id=${companyId}&cycle_id=${activeCycleId}`);
    setMetricsCycle(m.data);
  };
  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const url = activeCycleId
        ? `/analytics/ai-insights?company_id=${companyId}&cycle_id=${activeCycleId}`
        : `/analytics/ai-insights?company_id=${companyId}`;
      const r = await api.get(url);
      setInsights(r.data);
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => {
    loadCompany();
    loadCycles();
    loadConsolidated();
  }, [companyId]);

  // Reload consolidated whenever audit-type filter changes
  useEffect(() => {
    if (companyId) loadConsolidated();
  }, [auditType]);

  // When cycles load or auditType changes, snap activeCycleId to the newest cycle of that type
  useEffect(() => {
    if (!cycles.length) {
      setActiveCycleId(null);
      return;
    }
    const inType = cycles.filter((c) => (c.cycle_type || "internal") === auditType);
    if (!inType.length) {
      setActiveCycleId(null);
      return;
    }
    // If current active is not in this type, or no active, select last (newest, backend sorts asc)
    const stillValid = inType.find((c) => c.id === activeCycleId);
    if (!stillValid) setActiveCycleId(inType[inType.length - 1].id);
  }, [cycles, auditType]);

  useEffect(() => {
    if (activeCycleId) {
      loadCycleMetrics();
      loadInsights();
    }
  }, [activeCycleId]);

  const createCycle = async (e) => {
    e.preventDefault();
    try {
      if (newCycleType === "statutory") {
        const fy = (newCycleFY || "").trim();
        if (!/^FY\s?\d{4}-\d{2}$/.test(fy)) {
          toast.error("Enter fiscal year like 'FY 2025-26'");
          return;
        }
        // Derive period from FY start year for sorting purposes (Apr-Mar → period end = March, next year)
        const startYear = parseInt(fy.replace(/FY\s?/, "").split("-")[0], 10);
        await api.post("/cycles", {
          company_id: companyId,
          name: fy,
          period_month: 3,
          period_year: startYear + 1,
          cycle_type: "statutory",
          fiscal_year: fy,
        });
        toast.success("Statutory audit cycle created");
        setAuditType("statutory");
      } else {
        const monthIdx = MONTHS.indexOf(newCycleMonth) + 1;
        await api.post("/cycles", {
          company_id: companyId,
          name: `${newCycleMonth} ${newCycleYear}`,
          period_month: monthIdx,
          period_year: Number(newCycleYear),
          cycle_type: "internal",
        });
        toast.success("Internal audit cycle created");
        setAuditType("internal");
      }
      setOpenCycle(false);
      loadCycles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create cycle");
    }
  };

  const rollforward = async () => {
    if (!activeCycleId) return;
    try {
      const r = await api.post(`/cycles/${activeCycleId}/rollforward`);
      toast.success(`${r.data.rolled_forward} unresolved observation(s) rolled forward`);
      loadCycleMetrics();
      loadConsolidated();
      window.dispatchEvent(new Event("observations-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Roll-forward failed");
    }
  };

  const downloadReport = async (fmt) => {
    if (!activeCycleId) return;
    const includeRF = window.confirm("Include rolled-forward (unresolved) observations in this report?");
    const url = `/export/${fmt}?cycle_id=${activeCycleId}&include_rollforward=${includeRF}`;
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cycleName = cycles.find((c) => c.id === activeCycleId)?.name || "report";
      const isStat = (cycles.find((c) => c.id === activeCycleId)?.cycle_type || "internal") === "statutory";
      const prefix =
        fmt === "excel"
          ? "Audit_Observations"
          : isStat
          ? "Statutory_Audit_Report"
          : "Internal_Audit_Report";
      link.download = `${prefix}_${cycleName.replace(/\s+/g, "_")}.${fmt === "excel" ? "xlsx" : "pdf"}`;
      link.click();
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const header = (
    <div className="flex items-center justify-between">
      <div className="min-w-0">
        <Link to="/audit/companies" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Clients
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight mt-1 truncate" style={{ fontFamily: "Geist" }}>
          {company?.name || "Loading..."}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{company?.industry || "—"}</p>
      </div>
      <div className="flex items-center gap-2">
        {/* Audit-type segmented switch */}
        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="audit-type-switch">
          {[
            { k: "internal", label: "Internal" },
            { k: "statutory", label: "Statutory" },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setAuditType(opt.k)}
              className={
                "px-3 py-1.5 text-xs font-medium rounded transition-colors " +
                (auditType === opt.k
                  ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                  : "text-slate-500 hover:text-slate-800")
              }
              data-testid={`audit-type-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <Select value={activeCycleId || ""} onValueChange={setActiveCycleId}>
          <SelectTrigger className="w-[200px]" data-testid="cycle-selector">
            <CalendarBlank size={14} className="mr-1" />
            <SelectValue placeholder={filteredCycles.length ? "Select cycle" : `No ${auditType} cycles`} />
          </SelectTrigger>
          <SelectContent>
            {filteredCycles.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`cycle-option-${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Dialog open={openCycle} onOpenChange={(v) => { setOpenCycle(v); if (v) setNewCycleType(auditType); }}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="new-cycle-btn">
              <Plus size={14} className="mr-1" /> Cycle
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-cycle-dialog">
            <DialogHeader>
              <DialogTitle>New audit cycle</DialogTitle>
            </DialogHeader>
            <form onSubmit={createCycle} className="space-y-4">
              <div>
                <Label>Audit type</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[
                    { k: "internal", label: "Internal (monthly)", hint: "Progressive by month" },
                    { k: "statutory", label: "Statutory (annual)", hint: "Fiscal Year Apr–Mar" },
                  ].map((opt) => (
                    <button
                      key={opt.k}
                      type="button"
                      onClick={() => setNewCycleType(opt.k)}
                      className={
                        "text-left px-3 py-2 rounded border transition-colors " +
                        (newCycleType === opt.k
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400")
                      }
                      data-testid={`new-cycle-type-${opt.k}`}
                    >
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className={"text-[10px] mt-0.5 " + (newCycleType === opt.k ? "text-slate-300" : "text-slate-500")}>
                        {opt.hint}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              {newCycleType === "internal" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Month</Label>
                    <Select value={newCycleMonth} onValueChange={setNewCycleMonth}>
                      <SelectTrigger data-testid="cycle-month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Year</Label>
                    <Input type="number" value={newCycleYear} onChange={(e) => setNewCycleYear(e.target.value)} data-testid="cycle-year-input" />
                  </div>
                </div>
              ) : (
                <div>
                  <Label>Fiscal Year</Label>
                  <Input
                    placeholder="FY 2025-26"
                    value={newCycleFY}
                    onChange={(e) => setNewCycleFY(e.target.value)}
                    data-testid="cycle-fy-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Format: FY 2025-26 · covers April 2025 – March 2026</p>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="cycle-save-btn">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        {activeCycleId && activeCycleType === "internal" && (
          <Button variant="outline" onClick={rollforward} data-testid="rollforward-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Roll Forward
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" data-testid="company-actions-menu">
              <DotsThreeVertical size={16} weight="bold" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={openEdit} data-testid="edit-company-action">
              <PencilSimple size={13} className="mr-2" /> Edit client
            </DropdownMenuItem>
            {activeCycleId && (
              <DropdownMenuItem onClick={openCycleEdit} data-testid="edit-cycle-action">
                <PencilSimple size={13} className="mr-2" /> Edit report metadata (Prepared/Reviewed by)
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={deleteCompanyAction}
              className="text-red-600 focus:text-red-700"
              data-testid="delete-company-action"
            >
              <Trash size={13} className="mr-2" /> Delete client
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <AppLayout header={header}>
      {cycles.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="empty-cycles">
          <CalendarBlank size={32} className="mx-auto text-slate-400" />
          <h3 className="text-base font-semibold mt-3">No audit cycles yet</h3>
          <p className="text-sm text-slate-500 mt-1">Create the first cycle (e.g. April 2026) to begin recording observations.</p>
        </div>
      ) : filteredCycles.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="empty-cycles-for-type">
          <CalendarBlank size={32} className="mx-auto text-slate-400" />
          <h3 className="text-base font-semibold mt-3">
            No {auditType === "statutory" ? "statutory" : "internal"} audit cycles yet
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {auditType === "statutory"
              ? "Click 'Cycle' above and switch to Statutory to add an annual audit (e.g. FY 2025-26)."
              : "Click 'Cycle' above to add a monthly internal audit."}
          </p>
        </div>
      ) : (
        <>
          <AIInsightsPanel
            insights={insights}
            loading={insightsLoading}
            metrics={tab === "consolidated" ? metricsConsolidated : metricsCycle}
          />

          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <TabsList data-testid="company-tabs">
              <TabsTrigger value="observations" data-testid="tab-observations">Observations</TabsTrigger>
              <TabsTrigger value="cycle-matrix" data-testid="tab-cycle-matrix">Cycle Risk Matrix</TabsTrigger>
              <TabsTrigger value="consolidated" data-testid="tab-consolidated">Consolidated Matrix</TabsTrigger>
              <TabsTrigger value="profile" data-testid="tab-profile">Client Profile</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
            </TabsList>

            <TabsContent value="observations" className="mt-4">
              <ObservationsTable
                companyId={companyId}
                cycleId={activeCycleId}
                cycles={cycles}
                role="audit_team"
                onChange={() => {
                  loadCycleMetrics();
                  loadConsolidated();
                  loadInsights();
                }}
                onExport={downloadReport}
              />
            </TabsContent>

            <TabsContent value="cycle-matrix" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <h3 className="text-base font-semibold" style={{ fontFamily: "Geist" }}>Risk Distribution — {cycles.find(c=>c.id===activeCycleId)?.name}</h3>
                  <RiskBarChart counts={metricsCycle?.counts_by_risk} />
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <h3 className="text-base font-semibold" style={{ fontFamily: "Geist" }}>Status Snapshot</h3>
                  <div className="mt-4 space-y-2">
                    {Object.entries(metricsCycle?.counts_by_status || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{k}</span>
                        <span className="font-semibold tabular-nums">{v}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Inherent</div>
                      <div className="text-2xl font-semibold" style={{ fontFamily: "Geist" }}>{metricsCycle?.inherent_risk_score || 0}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Residual</div>
                      <div className="text-2xl font-semibold text-blue-700" style={{ fontFamily: "Geist" }}>{metricsCycle?.residual_risk_score || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="consolidated" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <h3 className="text-base font-semibold" style={{ fontFamily: "Geist" }}>All-Cycle Risk Distribution</h3>
                  <RiskBarChart counts={metricsConsolidated?.counts_by_risk} />
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <h3 className="text-base font-semibold" style={{ fontFamily: "Geist" }}>Inherent vs Residual per Cycle</h3>
                  <CycleTrendChart perCycle={metricsConsolidated?.per_cycle} />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <h3 className="text-base font-semibold mb-3" style={{ fontFamily: "Geist" }}>Cycle-by-Cycle</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2">Cycle</th>
                        <th className="text-right py-2">High</th>
                        <th className="text-right py-2">Medium</th>
                        <th className="text-right py-2">Low</th>
                        <th className="text-right py-2">Inherent</th>
                        <th className="text-right py-2">Residual</th>
                        <th className="text-right py-2">Reduction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(metricsConsolidated?.per_cycle || []).map((c) => (
                        <tr key={c.cycle_id} className="border-b border-slate-100">
                          <td className="py-2 font-medium">{c.cycle_name}</td>
                          <td className="text-right text-red-700 font-semibold">{c.counts_by_risk.High}</td>
                          <td className="text-right text-amber-700 font-semibold">{c.counts_by_risk.Medium}</td>
                          <td className="text-right text-emerald-700 font-semibold">{c.counts_by_risk.Low}</td>
                          <td className="text-right tabular-nums">{c.inherent_risk_score}</td>
                          <td className="text-right tabular-nums text-blue-700 font-semibold">{c.residual_risk_score}</td>
                          <td className="text-right tabular-nums text-emerald-700">{c.reduction_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="activity" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold" style={{ fontFamily: "Geist" }}>Activity Timeline</h3>
                  <p className="text-xs text-slate-500">Full audit log of every change · most recent first</p>
                </div>
                <ActivityTimeline companyId={companyId} />
              </div>
            </TabsContent>

            <TabsContent value="profile" className="mt-4">
              <ClientProfileTab
                company={company}
                onCompanyUpdate={(c) => setCompany(c)}
                role="audit_team"
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="company-edit-dialog">
          <DialogHeader><DialogTitle>Edit client</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div>
              <Label>Company Name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} required data-testid="company-edit-name" />
            </div>
            <div>
              <Label>Industry</Label>
              <Input value={editIndustry} onChange={(e) => setEditIndustry(e.target.value)} data-testid="company-edit-industry" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="company-edit-save">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={cycleEditOpen} onOpenChange={setCycleEditOpen}>
        <DialogContent data-testid="cycle-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit PDF report metadata</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            These values appear on the cover page of the PDF for{" "}
            <span className="font-semibold">{cycles.find((c) => c.id === activeCycleId)?.name || ""}</span>.
            Leave blank to use defaults.
          </p>
          <form onSubmit={saveCycleEdit} className="space-y-3 mt-3">
            <div>
              <Label>Prepared By</Label>
              <Input
                value={cyclePreparedBy}
                onChange={(e) => setCyclePreparedBy(e.target.value)}
                placeholder="e.g. Gunjan Jain"
                data-testid="cycle-prepared-by"
              />
            </div>
            <div>
              <Label>Reviewed By</Label>
              <Input
                value={cycleReviewedBy}
                onChange={(e) => setCycleReviewedBy(e.target.value)}
                placeholder="e.g. Shubham Garg"
                data-testid="cycle-reviewed-by"
              />
            </div>
            <div>
              <Label>Report Status Label</Label>
              <Input
                value={cycleStatusLabel}
                onChange={(e) => setCycleStatusLabel(e.target.value)}
                placeholder="Final"
                data-testid="cycle-status-label"
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="cycle-edit-save">
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
